import { useLingui } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';
import { useParams } from '@tanstack/react-router';
import { subDays } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { AdsSection } from '~/components/Ads/AdsSection';
import { AdsProvider } from '~/components/Ads/context';
import { ChannelPerformanceSection } from '~/components/ChannelPerformance/ChannelPerformanceSection';
import { createDateFilter } from '~/components/ChannelPerformance/selectors/DurationSelector';
import { ChannelPerformanceTableMobile } from '~/components/ChannelPerformance/views/TableMobile';
import { ExternalStatisticsPageSection } from '~/components/ExternalStatistics/ExternalStatisticsPageSection';
import { Header } from '~/components/Header/Header';
import { ModalComponent } from '~/components/Modal/Modal';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { ProgressSection } from '~/components/ProgressSection/ProgressSection';
import { fetchSummary, useSummary } from '~/hooks/api/useSummary';
import { useIsMobile } from '~/hooks/useIsMobile';
import { ChannelType, DateFilter } from '~/types';
import { wholePeriodDateFilterFromSummary } from '~/utils/dateUtils';
import { fetchToken } from '~/utils/fetchToken';
import { queryClient } from '~/utils/queryClient';

const SUMMARY_STALE_MS = 15 * 60 * 1000;

export const Route = createFileRoute('/$companyUuid/$reportId')({
  loader: async ({ params }) => {
    await queryClient.ensureQueryData({
      queryKey: ['token'],
      queryFn: () => fetchToken(params.companyUuid),
      retry: 3,
      retryDelay: (attempt) => Math.min(attempt * 1000, 3000),
      staleTime: 6 * 60 * 60 * 1000,
    });

    await queryClient.ensureQueryData({
      queryKey: ['summary', params.reportId],
      queryFn: () => fetchSummary(params.reportId),
      staleTime: SUMMARY_STALE_MS,
    });
  },
  component: RouteComponent,
  validateSearch: z.object({
    page: z.union([z.string(), z.number()]).optional(),
    perPage: z.union([z.string(), z.number()]).optional(),
    sortBy: z.string().optional(),
    sortOrder: z.enum(['asc', 'desc']).optional(),
  }),
});

function RouteComponent() {
  const params = useParams({ strict: false });
  const isMobile = useIsMobile();
  const { data: summaryData } = useSummary(params.reportId);

  const isProperty = summaryData?.promotable_type === 'existing_home';

  const { t } = useLingui();

  const [dateFilter, setDateFilter] = useState<DateFilter>(
    createDateFilter(30, t`Last 30 days`, 'last-30-days').getFilter(),
  );
  const hasAutoSwitched = useRef(false);

  useEffect(() => {
    if (hasAutoSwitched.current || !summaryData) return;
    hasAutoSwitched.current = true;

    const wholePeriod = wholePeriodDateFilterFromSummary(summaryData, t`Whole period`);
    if (!wholePeriod) return;

    if (wholePeriod.endDate < subDays(new Date(), 30)) {
      setDateFilter(wholePeriod);
    }
  }, [summaryData, t]);

  const [selectedChannel, setSelectedChannel] = useState<ChannelType>('all');

  return (
    <PageLayout>
      <Header
        onChannelChange={setSelectedChannel}
        dateFilter={dateFilter}
        onDateFilterChange={setDateFilter}
      />

      <ProgressSection />

      <ChannelPerformanceSection dateFilter={dateFilter} selectedChannel={selectedChannel} />
      {isMobile ? <ChannelPerformanceTableMobile dateFilter={dateFilter} /> : null}

      <AdsProvider selectedChannel={selectedChannel}>
        <AdsSection selectedChannel={selectedChannel} />

        {isProperty && summaryData?.location?.country_code === 'NO' ? (
          <ExternalStatisticsPageSection dateFilter={dateFilter} />
        ) : null}

        <ModalComponent />
      </AdsProvider>
    </PageLayout>
  );
}
